import {useCallback, useEffect, useMemo, useState} from "react";
import {useLocale} from "i18n";
import {useUser} from "UserProvider";
import {useMountedOrigin} from "utils/useMountedOrigin";
import {fetchReferralSummary, type ReferralSummary} from "./ReferralApi";
import {referralCardModel, type ReferralCardModel} from "./referralCard";
import {referrerRewardStoryForUser} from "./referrerReward";

export interface ReferralCardState {
	model: ReferralCardModel | null;
	loading: boolean;
	failed: boolean;
	reload: () => void;
}

export interface ReferralCardOptions {
	/**
	 * Whether to ask the api at all (ui#1180). `GET /account/referral` is an account endpoint, so a
	 * caller that already knows the visitor has no account — the success screen's referral line, which
	 * renders nothing for an anonymous sender — would otherwise fire a guaranteed 401 on every mount of
	 * a surface nobody sees. Defaults to `true` for the account card, which only mounts behind a login.
	 */
	enabled?: boolean;
}

/**
 * Thin React adapter over the referral summary (epic #26): holds request state, calls the service, and
 * hands the domain's view model to a dumb card. It contains no rules — every number and every "should this
 * line render" decision lives in {@link referralCardModel} and `referral/referralPrompt.ts`.
 *
 * The SUMMARY is what the request produces and what this holds; the MODEL is derived from it. That
 * split is what keeps the presentation inputs — the reader's language, and since ui#1517 the browser's
 * origin — out of the fetch: both change after mount (the origin on the very first mount, every time),
 * and having them in the request's dependencies asked the api for the same unchanged numbers twice.
 */
export function useReferralCard({enabled = true}: ReferralCardOptions = {}): ReferralCardState {
	const {locale} = useLocale();
	// ui#1693: WHICH reward the copy names, decided entirely in `referral/referrerReward.ts`. This hook
	// reads the user and forwards the answer — it does not look at the plan itself, so there is no
	// second reading of "is this account really paying" to drift from the one that module owns.
	const [user] = useUser();
	const rewardStory = referrerRewardStoryForUser(user);
	// ui#1517: the host this advocate is on, so the share link stays on this site without a brand
	// catalogue. `""` until mount, which `referralShareLink` answers with no link.
	const currentOrigin = useMountedOrigin();
	const [summary, setSummary] = useState<ReferralSummary | null>(null);
	// A disabled hook is not "loading": it is never going to resolve, and a caller that renders a
	// spinner while `loading` would spin forever.
	const [loading, setLoading] = useState(enabled);
	const [failed, setFailed] = useState(false);
	const [reloadToken, setReloadToken] = useState(0);

	useEffect(() => {
		if (!enabled) {
			setSummary(null);
			setLoading(false);
			setFailed(false);
			return;
		}
		let active = true;
		setLoading(true);
		setFailed(false);
		fetchReferralSummary()
			.then((next) => {
				if (!active) return;
				setSummary(next);
			})
			.catch(() => {
				if (!active) return;
				// A failed card must say so rather than render an empty one: "0 referrals" and "we could not
				// load this" are different facts, and the first one is a lie the advocate would act on.
				setFailed(true);
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [enabled, reloadToken]);

	const model = useMemo(
		() => (summary === null || failed
			? null
			: referralCardModel(summary, locale, currentOrigin, rewardStory)),
		[currentOrigin, failed, locale, rewardStory, summary],
	);

	const reload = useCallback(() => setReloadToken((token) => token + 1), []);
	return {model, loading, failed, reload};
}
